import { z } from 'zod';

/**
 * Browser configuration, resolved at RUNTIME rather than baked in at build time.
 *
 * This matters more than it looks. §2 requires that the same built artifact runs
 * in dev, staging and production — but a Vite build inlines `import.meta.env`
 * values into the bundle, which would mean one image per environment and a
 * rebuild to point staging at a different identity broker. So the container
 * serves `/konfig.json`, written by its entrypoint from environment variables,
 * and the app reads that. One image, promoted unchanged from staging to
 * production.
 *
 * Local development has no such file, so it falls back to Vite's env vars.
 *
 * Everything here is public by definition — it ships to every user's browser.
 * There is deliberately no client secret: the SPA is a PUBLIC OIDC client using
 * PKCE, because a secret embedded in downloadable JavaScript is not a secret.
 */
const skjema = z.object({
  apiBaseUrl: z.url(),
  oidcIssuerUrl: z.url(),
  oidcClientId: z.string().min(1),
});

export type Miljo = z.infer<typeof skjema>;

let bufret: Miljo | null = null;

async function hentFraServer(): Promise<unknown> {
  try {
    const svar = await fetch('/konfig.json', { cache: 'no-store' });
    if (!svar.ok) return null;
    return await svar.json();
  } catch {
    // No runtime config served (local `vite dev`) — fall back to build-time env.
    return null;
  }
}

export async function lastMiljo(): Promise<Miljo> {
  if (bufret) return bufret;

  // `import.meta.env` is typed with an `any` index signature; narrowing it once
  // here keeps that `any` from spreading into the parsed configuration.
  const viteMiljo = import.meta.env as Record<string, string | undefined>;

  const kilde = (await hentFraServer()) ?? {
    apiBaseUrl: viteMiljo.VITE_API_BASE_URL,
    oidcIssuerUrl: viteMiljo.VITE_OIDC_ISSUER_URL,
    oidcClientId: viteMiljo.VITE_OIDC_CLIENT_ID,
  };

  const resultat = skjema.safeParse(kilde);
  if (!resultat.success) {
    // Fails at startup with a readable message instead of a blank screen and an
    // undefined URL somewhere inside a fetch call.
    throw new Error(
      `Ugyldig frontend-konfigurasjon:\n${resultat.error.issues
        .map((i) => `  ${i.path.join('.')}: ${i.message}`)
        .join('\n')}`,
    );
  }

  bufret = resultat.data;
  return bufret;
}

/**
 * The resolved configuration.
 *
 * Throws if called before `lastMiljo()` has completed — which cannot happen in
 * the app, because `main.tsx` awaits it before rendering anything.
 */
export function miljo(): Miljo {
  if (!bufret) {
    throw new Error('Miljøkonfigurasjonen er ikke lastet ennå. Kall lastMiljo() først.');
  }
  return bufret;
}

/** Test seam — lets a unit test supply configuration without a server. */
export function settMiljoForTest(verdier: Miljo): void {
  bufret = skjema.parse(verdier);
}
